import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Square, SquarePen, Info, Menu } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArcheLayout } from '../../components/ArcheLayout';
import { useSearchResults } from '../../context/SearchResultsContext';
import { useSidebar } from '../../context/SidebarContext';
import { useFiasAI } from './hooks/useFiasAI';
import { ModeToggleBar } from './components/ModeToggleBar';
import { MessageBubble } from './components/MessageBubble';
import { ArcheResultsBlock } from './components/ArcheResultsBlock';
import { ArcheSuggestionsRow } from './components/ArcheSuggestionsRow';

function MakeARequestContent() {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { setSearchResults, clearSearchResults } = useSearchResults();
  const { isCollapsed, isMobile, toggle, setCollapsed } = useSidebar();

  const {
    mode,
    setSearchEnabled,
    setAIEnabled,
    messages,
    submit,
    stop,
    reset,
    isStreaming,
    isSearching,
    latestSearchResults,
    latestSearchQuery,
  } = useFiasAI();

  const isBusy = isStreaming || isSearching;

  // Open sidebar by default on desktop
  useEffect(() => {
    if (!isMobile) {
      setCollapsed(false);
    }
  }, [isMobile, setCollapsed]);

  // Sync search results to sidebar context
  useEffect(() => {
    if (latestSearchResults && latestSearchResults.length > 0) {
      setSearchResults(
        latestSearchResults.map((r) => ({
          archId: r.archId,
          slug: null,
          name: r.name,
          simpleDescription: r.simpleDescription,
          imageRef: r.imageRef,
          categoryId: null,
          categoryName: null,
          categorySlug: null,
          tags: [],
          fitnessScore: r.fitnessScore,
          invocationCount: 0,
          pricing: { model: 'free' as const, currency: 'usd' as const },
          hasPerUseCredits: false,
          isStatic: false,
          archeType: 'tool' as const,
          renderMode: 'default' as const,
          relevance: r.relevance,
          contributorId: '',
          isPublic: true,
          isFullScreen: false,
        })),
        latestSearchQuery || 'search',
      );
    }
  }, [latestSearchResults, latestSearchQuery, setSearchResults]);

  // Reset conversation
  const handleReset = useCallback(() => {
    reset();
    setInputValue('');
    clearSearchResults();
    if (!isMobile) {
      textareaRef.current?.focus();
    }
  }, [reset, clearSearchResults, isMobile]);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus textarea when not busy (skip on mobile)
  useEffect(() => {
    if (!isBusy && !isMobile) {
      textareaRef.current?.focus();
    }
  }, [isBusy, isMobile]);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isBusy) return;

    setInputValue('');
    await submit(trimmedInput);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-4 py-3 flex items-center gap-4">
          {/* Left: sidebar toggle + title */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Button
              variant="outline"
              size="icon"
              onClick={toggle}
              className={`h-11 w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
              title="Open sidebar"
            >
              <Menu className="h-6 w-6" />
            </Button>
            <h1 className="text-lg font-semibold leading-tight">Make a Re-Quest</h1>
            <Link
              to="/marketplace/arches/arche_make_a_request"
              className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
              title="View arche details"
            >
              <Info className="h-5 w-5" />
            </Link>
          </div>

          {/* Center: mode toggles */}
          <ModeToggleBar
            searchEnabled={mode.searchEnabled}
            aiEnabled={mode.aiEnabled}
            onSearchToggle={setSearchEnabled}
            onAIToggle={setAIEnabled}
            disabled={isBusy}
          />

          {/* Right: actions */}
          <div className="flex items-center gap-3 flex-1 justify-end">
            <Button variant="outline" size="sm" onClick={() => navigate('/a/arche_favorites')}>
              Desktop
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isBusy || messages.length === 0}
              className="gap-1.5 [&_svg]:size-5!"
            >
              <SquarePen />
              New
            </Button>
          </div>
        </div>
      </div>

      {messages.length === 0 ? (
        <>
          {/* Empty state: centered content with input */}
          <div className="flex-1 flex flex-col">
            {/* Centered logo, title, and input */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="text-center mb-6">
                <img
                  src="/fias-logo-image.png"
                  alt="Fias AI"
                  className="h-16 w-16 mb-4 mx-auto dark:invert dark:brightness-90"
                />
                <h2 className="text-xl font-medium text-foreground mb-2">Make a Re-Quest</h2>
                <p className="text-muted-foreground">Ask Fias AI Anything and Search for Arches.</p>
                <p className="text-muted-foreground/60 text-sm mt-2">
                  Use the toggle buttons above to turn Fias AI or Arche search on and off.
                </p>
              </div>
              <div className="max-w-3xl mx-auto px-4 w-full">
                <form onSubmit={handleSubmit} className="relative">
                  <Textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      mode.aiEnabled ? 'Ask Fias AI anything...' : 'What is your Re-Quest?'
                    }
                    disabled={isBusy}
                    className="min-h-[100px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto text-base"
                    rows={3}
                  />
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!inputValue.trim() || isBusy}
                    className="absolute right-2 bottom-3 h-10 w-10 rounded-xl"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Press Enter to send, Shift+Enter for new line
                </p>
              </div>
            </div>

            {/* Bottom-pinned social icons and legal links */}
            <div className="flex-shrink-0 pb-4">
              <div className="flex items-center justify-center gap-5 mt-3">
                <a
                  href="https://x.com/tomlucient"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="X"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a
                  href="https://linkedin.com/company/fiasai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="LinkedIn"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a
                  href="https://fias.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="Fias.io"
                >
                  <img src="/fias-logo-image.png" alt="Fias" className="h-6 w-6 dark:invert" />
                </a>
                <a
                  href="https://t.me/FiasAI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="Telegram"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/playlist?list=PLQdfOryRxAcel4oLrZX6Y_89JV4q030mZ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  title="YouTube"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
              </div>
              <div className="text-center text-xs text-muted-foreground mt-2 space-x-3">
                <Link to="/legal/terms" className="hover:text-foreground transition-colors">
                  Terms of Service
                </Link>
                <span>·</span>
                <Link to="/legal/privacy" className="hover:text-foreground transition-colors">
                  Privacy Policy
                </Link>
                <span>·</span>
                <Link
                  to="/legal/acceptable-use"
                  className="hover:text-foreground transition-colors"
                >
                  Acceptable Use Policy
                </Link>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Conversation: messages area */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-4 py-6">
              <div className="space-y-6">
                {messages.map((message) => (
                  <div key={message.id}>
                    <MessageBubble
                      role={message.role}
                      content={message.content}
                      isStreaming={message.isStreaming}
                      modelInfo={message.modelInfo}
                    />
                    {/* AI mode: compact suggestion row after completed messages */}
                    {mode.aiEnabled &&
                      message.role === 'assistant' &&
                      !message.isStreaming &&
                      (() => {
                        const suggestions = message.recommendedArches ?? message.searchResults;
                        return suggestions && suggestions.length > 0 ? (
                          <ArcheSuggestionsRow
                            suggestions={suggestions.slice(0, 3)}
                            onArcheClick={(archId) => navigate(`/a/${archId}`)}
                          />
                        ) : null;
                      })()}
                    {/* In search-only mode, show Arche results in the main panel */}
                    {!mode.aiEnabled && message.role === 'assistant' && message.searchResults && (
                      <ArcheResultsBlock
                        results={message.searchResults.slice(0, 3)}
                        totalResults={message.searchResults.length}
                        onArcheClick={(archId) => navigate(`/a/${archId}`)}
                      />
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>

          {/* Bottom input bar */}
          <div className="flex-shrink-0 border-t border-border bg-background">
            <div className="max-w-3xl mx-auto px-4 py-4">
              <form onSubmit={handleSubmit} className="relative">
                <Textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    mode.aiEnabled ? 'Ask Fias AI anything...' : 'What is your Re-Quest?'
                  }
                  disabled={isBusy}
                  className="min-h-[44px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto"
                  rows={1}
                  style={{ height: 'auto' }}
                />
                {isStreaming ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    onClick={stop}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!inputValue.trim() || isBusy}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                )}
              </form>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Press Enter to send, Shift+Enter for new line
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function MakeARequestPage() {
  return (
    <ArcheLayout>
      <MakeARequestContent />
    </ArcheLayout>
  );
}

export default MakeARequestPage;
